import React, { useEffect, useState } from "react";
import api from "../../services/api";

const money = value =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value || 0));

const percent = value => `${Number(value || 0).toFixed(2)}%`;

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)"
};

const ExecutiveReportDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    conversion: {},
    audit: {},
    goals: {},
    followUp: {}
  });

  const loadData = async () => {
    try {
      setLoading(true);

      const month = currentMonth();

      const [conversionRes, auditRes, goalsRes, followUpRes] = await Promise.all([
        api.get("/dashboard/conversion"),
        api.get("/dashboard/seller-audit"),
        api.get(`/dashboard/seller-goals?month=${month}`),
        api.get("/dashboard/follow-up")
      ]);

      setData({
        conversion: conversionRes.data || {},
        audit: auditRes.data || {},
        goals: goalsRes.data || {},
        followUp: followUpRes.data || {}
      });
    } catch (err) {
      console.error("Erro ao carregar Relatório Executivo:", err);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const conversionSummary = data.conversion.summary || {};
    const auditSummary = data.audit.summary || {};
    const goalsSummary = data.goals.summary || {};
    const followSummary = data.followUp.summary || {};
    const sellers = data.audit.sellers || [];

    const rows = [
      ["Relatório Executivo Comercial"],
      ["Gerado em", new Date().toLocaleString("pt-BR")],
      [],
      ["Indicador", "Valor"],
      ["Valor vendido", money(auditSummary.salesAmount || conversionSummary.salesAmount)],
      ["Vendas realizadas", auditSummary.salesCount || conversionSummary.salesCount || 0],
      ["Taxa de conversão", percent(auditSummary.conversionRate || conversionSummary.conversionRate)],
      ["Ticket médio", money(auditSummary.averageTicket || conversionSummary.averageTicket)],
      ["Meta total", money(goalsSummary.totalGoal)],
      ["Falta para meta", money(goalsSummary.totalMissing)],
      ["Atingimento da meta", percent(goalsSummary.progressPercentage)],
      ["Leads parados", auditSummary.stoppedTickets || 0],
      ["Leads sem resposta", auditSummary.unansweredTickets || 0],
      ["Follow-ups atrasados", followSummary.overdueFollowUps || 0],
      ["Follow-ups de hoje", followSummary.todayFollowUps || 0],
      ["Leads sem próxima ação", followSummary.leadsWithoutFollowUp || 0],
      [],
      ["Ranking de vendedores"],
      ["Vendedor", "Vendas", "Valor vendido", "Conversão", "Mensagens", "Leads parados"]
    ];

    sellers.forEach(seller => {
      rows.push([
        seller.name,
        seller.salesCount || 0,
        money(seller.salesAmount),
        percent(seller.conversionRate),
        seller.messagesSent || 0,
        seller.stoppedTickets || 0
      ]);
    });

    const csv = rows
      .map(row =>
        row
          .map(cell => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(";")
      )
      .join("\n");

    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;"
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", `relatorio-executivo-comercial-${currentMonth()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    loadData();
  }, []);

  const conversionSummary = data.conversion.summary || {};
  const auditSummary = data.audit.summary || {};
  const goalsSummary = data.goals.summary || {};
  const followSummary = data.followUp.summary || {};
  const sellers = data.audit.sellers || [];

  const salesAmount = auditSummary.salesAmount || conversionSummary.salesAmount || 0;
  const salesCount = auditSummary.salesCount || conversionSummary.salesCount || 0;
  const conversionRate = auditSummary.conversionRate || conversionSummary.conversionRate || 0;
  const averageTicket = auditSummary.averageTicket || conversionSummary.averageTicket || 0;

  return (
    <div style={{ marginTop: 24, marginBottom: 32, gridColumn: "1 / -1", width: "100%" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #020617 0%, #7f1d1d 55%, #dc2626 100%)",
          borderRadius: 24,
          padding: 24,
          color: "#fff",
          boxShadow: "0 20px 45px rgba(220, 38, 38, 0.22)",
          marginBottom: 18
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 700 }}>
              Visão Executiva
            </div>

            <h2 style={{ margin: "6px 0 4px", fontSize: 28, fontWeight: 900 }}>
              Relatório Executivo Comercial
            </h2>

            <div style={{ opacity: 0.9 }}>
              Resumo consolidado de vendas, metas, conversão, follow-ups e riscos comerciais.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
            <button
              onClick={loadData}
              disabled={loading}
              style={{
                border: 0,
                borderRadius: 12,
                padding: "11px 18px",
                fontWeight: 800,
                cursor: "pointer",
                background: "#fff",
                color: "#b91c1c"
              }}
            >
              {loading ? "Atualizando..." : "Atualizar"}
            </button>

            <button
              onClick={exportCSV}
              style={{
                border: 0,
                borderRadius: 12,
                padding: "11px 18px",
                fontWeight: 800,
                cursor: "pointer",
                background: "#16a34a",
                color: "#fff"
              }}
            >
              Exportar CSV
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 16 }}>
        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Valor vendido</div>
          <div style={{ color: "#16a34a", fontSize: 24, fontWeight: 900 }}>{money(salesAmount)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Vendas</div>
          <div style={{ color: "#0f172a", fontSize: 24, fontWeight: 900 }}>{salesCount}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Conversão</div>
          <div style={{ color: "#dc2626", fontSize: 24, fontWeight: 900 }}>{percent(conversionRate)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Ticket médio</div>
          <div style={{ color: "#0f172a", fontSize: 24, fontWeight: 900 }}>{money(averageTicket)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Meta total</div>
          <div style={{ color: "#7c3aed", fontSize: 24, fontWeight: 900 }}>{money(goalsSummary.totalGoal)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Falta para meta</div>
          <div style={{ color: "#ef4444", fontSize: 24, fontWeight: 900 }}>{money(goalsSummary.totalMissing)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Leads parados</div>
          <div style={{ color: "#ef4444", fontSize: 24, fontWeight: 900 }}>{auditSummary.stoppedTickets || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Follow-ups atrasados</div>
          <div style={{ color: "#f59e0b", fontSize: 24, fontWeight: 900 }}>{followSummary.overdueFollowUps || 0}</div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
          Ranking executivo
        </div>

        <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900, marginBottom: 14 }}>
          Vendedores por resultado comercial
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 850 }}>
            <thead>
              <tr style={{ color: "#64748b", textAlign: "left", fontSize: 12 }}>
                <th style={{ padding: "10px 8px" }}>#</th>
                <th style={{ padding: "10px 8px" }}>Vendedor</th>
                <th style={{ padding: "10px 8px" }}>Vendas</th>
                <th style={{ padding: "10px 8px" }}>Valor vendido</th>
                <th style={{ padding: "10px 8px" }}>Conversão</th>
                <th style={{ padding: "10px 8px" }}>Mensagens</th>
                <th style={{ padding: "10px 8px" }}>Leads parados</th>
              </tr>
            </thead>

            <tbody>
              {sellers.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: 16, color: "#64748b" }}>
                    Nenhum vendedor encontrado.
                  </td>
                </tr>
              ) : (
                sellers.slice(0, 12).map((seller, index) => (
                  <tr key={seller.userId || index} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "12px 8px", fontWeight: 900 }}>{index + 1}</td>
                    <td style={{ padding: "12px 8px", fontWeight: 800 }}>{seller.name}</td>
                    <td style={{ padding: "12px 8px" }}>{seller.salesCount || 0}</td>
                    <td style={{ padding: "12px 8px", color: "#16a34a", fontWeight: 900 }}>{money(seller.salesAmount)}</td>
                    <td style={{ padding: "12px 8px", color: "#dc2626", fontWeight: 900 }}>{percent(seller.conversionRate)}</td>
                    <td style={{ padding: "12px 8px", color: "#2563eb", fontWeight: 900 }}>{seller.messagesSent || 0}</td>
                    <td style={{ padding: "12px 8px", color: "#ef4444", fontWeight: 900 }}>{seller.stoppedTickets || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveReportDashboard;
